import { assignmentItemKey } from './practiceAssignments';
import { getCleanWord, shuffleArray } from './wordPracticeUtils';

const cleanText = (value) => String(value || '').trim();

export const selectTierValue = (value, tier = 'tier_6') => {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return '';
  const normalizedTier = String(tier).startsWith('tier_') ? String(tier) : `tier_${tier}`;
  const keys = [normalizedTier, normalizedTier.replace('tier_', 'tier')];
  for (const key of keys) {
    if (typeof value[key] === 'string' && value[key].trim()) return value[key];
  }
  return Object.values(value).find((candidate) => typeof candidate === 'string' && candidate.trim()) || '';
};

export const getDefinitionCn = (item) => cleanText(
  item?.definition?.cn
    || item?.definition?.zh
    || item?.definition_cn
    || item?.chinese
    || item?.meaning,
);

export const getDefinitionEn = (item) => cleanText(
  item?.definition?.en
    || item?.definition_en
    || item?.english_definition,
);

export const getComprehensiveExercises = (item) => (
  Array.isArray(item?.exercises) ? item.exercises : []
);

export const getTier6Sentence = (item) => {
  const comprehensiveStage = item?.comprehensive_training?.stages?.find(
    (stage) => [3, 4].includes(Number(stage?.stage)) && cleanText(stage?.sentence),
  );
  const direct = cleanText(
    item?.pronunciation_sentence
      || comprehensiveStage?.sentence
      || item?.tier_6_sentence
      || item?.tier6_sentence,
  );
  if (direct) return direct.replace(/_/g, ' ');
  const reordering = getComprehensiveExercises(item).find((exercise) => exercise?.type === 'sentence_reordering');
  const answer = cleanText(selectTierValue(reordering?.sentence_answer, 'tier_6'));
  if (answer) return answer.replace(/_/g, ' ');
  const sample = (item?.sample_sentences || []).find((candidate) => cleanText(candidate?.sentence));
  return cleanText(sample?.sentence);
};

export const normalizeComprehensiveItem = (item, fallbackMode = 'comprehensive') => {
  const source = item || {};
  const word = cleanText(source.word);
  return {
    ...source,
    word,
    sense_id: cleanText(source.sense_id) || 'primary',
    definition: {
      ...(source.definition || {}),
      cn: getDefinitionCn(source),
      en: getDefinitionEn(source),
    },
    definition_cn: getDefinitionCn(source),
    definition_en: getDefinitionEn(source),
    exercises: getComprehensiveExercises(source),
    sample_sentences: Array.isArray(source.sample_sentences) ? source.sample_sentences : [],
    training_mode: source.training_mode || fallbackMode,
    pronunciation_sentence: getTier6Sentence(source),
  };
};

export const comprehensiveItemKey = (item) => assignmentItemKey(item);

export const claimComprehensiveCompletion = (completedKeys, item) => {
  const key = comprehensiveItemKey(item);
  const current = completedKeys instanceof Set ? completedKeys : new Set(completedKeys || []);
  if (current.has(key)) return { claimed: false, key, keys: current };
  return { claimed: true, key, keys: new Set([...current, key]) };
};

export const buildComprehensiveQuestion = (item, options = []) => ({
  key: comprehensiveItemKey(item),
  word: item?.word || '',
  stage: 2,
  item: normalizeComprehensiveItem(item),
  options,
});

export const buildComprehensiveDictationChoices = (item, labels = []) => labels.map((label) => ({
  key: label,
  label,
  correct: label === getDefinitionCn(item),
}));

export const buildComprehensiveMeaningChoices = (target, items, random = shuffleArray) => {
  const correct = getDefinitionCn(target);
  const labels = [];
  const seen = new Set([correct]);
  const candidates = [
    ...(items || []).map((item) => getDefinitionCn(item)),
    ...((target?.choice_distractors || []).map((value) => cleanText(value))),
  ];
  candidates.forEach((label) => {
    if (label && !seen.has(label)) {
      seen.add(label);
      labels.push(label);
    }
  });
  return random([correct, ...labels.slice(0, 3)]);
};

export const normalizeSpelling = (value) => getCleanWord(value)
  .toLocaleLowerCase()
  .replace(/[\u2018\u2019\u02BC]/g, "'")
  .replace(/\s+/g, ' ')
  .trim();

export const isSpellingCorrect = (answer, expected) => (
  normalizeSpelling(answer) === normalizeSpelling(expected)
);

export const createComprehensiveProgress = (items) => (items || []).reduce((result, item) => {
  result[comprehensiveItemKey(item)] = {
    step: 1,
    status: 'learning',
    last_result: null,
  };
  return result;
}, {});

export const updateComprehensiveProgress = (progress, item, correct) => {
  const key = comprehensiveItemKey(item);
  const previous = progress?.[key] || { step: 1, status: 'learning', last_result: null };
  const nextStep = correct ? previous.step + 1 : 1;
  return {
    ...progress,
    [key]: {
      ...previous,
      step: correct ? Math.min(4, nextStep) : 1,
      status: correct && nextStep >= 4 ? 'mastered' : 'learning',
      last_result: correct ? 'correct' : 'incorrect',
    },
  };
};

export const completedComprehensiveCount = (progress) => Object.values(progress || {})
  .filter((entry) => entry?.status === 'mastered' || Number(entry?.step) >= 4).length;

export const completedComprehensiveSteps = (progress) => Object.values(progress || {})
  .reduce((total, entry) => {
    const step = Math.max(1, Math.min(4, Number(entry?.step) || 1));
    return total + Math.max(0, Math.min(3, step - 1));
  }, 0);

export const pendingComprehensiveItems = (items, progress) => (items || []).filter((item) => {
  const entry = progress?.[comprehensiveItemKey(item)] || {};
  return entry.status !== 'mastered' && Number(entry.step || 1) < 4;
});

export const nextComprehensiveActivity = (items, progress, cardCount = 3) => {
  const pending = pendingComprehensiveItems(items, progress);
  const meaningReady = pending.filter((item) => (
    Number(progress?.[comprehensiveItemKey(item)]?.step || 1) === 1
  ));
  const dictationReady = pending.filter((item) => (
    Number(progress?.[comprehensiveItemKey(item)]?.step || 1) === 2
  ));
  const oralReady = pending.filter((item) => (
    Number(progress?.[comprehensiveItemKey(item)]?.step || 1) === 3
  ));
  if (meaningReady.length) return { kind: 'practice', stage: 1, items: meaningReady };
  if (dictationReady.length) return { kind: 'practice', stage: 2, items: dictationReady };
  if (oralReady.length >= cardCount || (oralReady.length > 0 && !meaningReady.length && !dictationReady.length)) {
    return { kind: 'pronunciation', items: oralReady.slice(0, cardCount) };
  }
  return { kind: 'complete', items: [] };
};

export const chunkComprehensiveItems = (items, size = 9) => {
  const chunks = [];
  for (let index = 0; index < (items || []).length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

export const comprehensiveAudioRequests = (items) => {
  const requests = [];
  const seen = new Set();
  (items || []).forEach((item) => {
    const word = cleanText(getCleanWord(item?.word));
    const sentence = getTier6Sentence(item);
    [[word, 'word'], [sentence, 'sentence']].forEach(([text, mode]) => {
      const key = `${mode}:${text}`;
      if (text && !seen.has(key)) {
        seen.add(key);
        requests.push({ text, mode });
      }
    });
  });
  return requests;
};

export const pronunciationPassed = (payload) => {
  const score = Number(payload?.score);
  return Number.isFinite(score) ? score >= 70 : Boolean(payload?.passed);
};
